import { useState, useEffect } from 'react';
import { ContextMenu, ContextMenuTrigger, hideMenu } from '@threeskye/react-contextmenu';
import { MenuItem } from 'react-contextmenu';
import { Node, Transforms, Editor as SlateEditor } from 'slate';
import { ReactEditor } from 'slate-react'
import { Trash2 } from 'react-feather';
import TableOfContentsLine from './TableOfContentsLine';
import DropDown from '../components/DropDown'
import Checkbox from '../components/Checkbox';


const TableOfContents = (props) => {
	const { editor, pageBreaks, manualPageBreaks, styles, flowStyles, startingPageNumber } = props
	const [headings, setHeadings] = useState([]);
	const [tocElement, setTocElement] = useState(null)
	const [hovering, setHovering] = useState(false)
	const [trackBefore, setTrackBefore] = useState(false)
	const [levels, setLevels] = useState([{ type: 'h1', indent: 0.00 }])
	const [levelTypes, setLevelTypes] = useState(["h1"])
	const [gapStyle, setGapStyle] = useState('dotted')
	const [h1Indent, setH1Indent] = useState(0)
	const [h2Indent, setH2Indent] = useState(0)
	const [h3Indent, setH3Indent] = useState(0)
	const [thePath, setThePath] = useState(null)
	const ToCId = Math.round(Math.random() * 1000000);
	const indentationOptions = [0.00, 18.00, 36.00, 54.00, 72.00, 90.00]
	const tocNode = props.element
	const tocPath = props.elementLocation

	// console.log("### TOC pageBreaks", pageBreaks)
	// console.log("### TOC manualPageBreaks", manualPageBreaks)
	// console.log("### TOC editor.children", editor.children)

	useEffect(() => {
		updateChildren(tocNode)
		if (thePath === null) setThePath(tocPath)
		const el = document.getElementById("actual-toc-" + ToCId)
		setTocElement(el)
	}, [tocPath, pageBreaks, manualPageBreaks])

	function isWhitespaceString(str) {
		return !str.replace(/\s/g, '').length
	}

	function getTOCLocation() {
		const id = props.element.id
		let location = [];
		for (const [node, path] of Node.descendants(editor)) {
			if (node.id && node.id === id) {
				location.push(path[0]);
			}
		}
		return location
	}

	function determinePage(node, idx, allBreaks) {
		const startPage = startingPageNumber || 2;
		let pageCount = startPage;
		allBreaks.forEach((pageBreak) => {
			if (pageBreak.at && pageBreak.at[0] <= idx) {
				pageCount++;
			};
		})
		return { ...node, page: pageCount }
	}

	function updatedAllHeadings(newChildren, close) {
		const tocHeadingIndexes = newChildren.map(h => h.headingIndex).filter(h => h !== undefined)

		function getElementsById(editor, string) {
			return Array.from(
				SlateEditor.nodes(editor, {
					at: [],
					match: n => !SlateEditor.isEditor(n) && n.id && n.id.startsWith(string) && n.type && n.type.startsWith('heading-'),
				})
			);
		}
		const headings = getElementsById(editor, 'heading-at');
		headings.forEach(([node, path]) => {
			if (!tocHeadingIndexes.includes(path[0])) {
				Transforms.setNodes(editor, { id: undefined }, { at: path });
			}
		});

		if (close) {
			hideMenu()
		}
	}

	function updateChildren(node, before) {
		const unsortedPageBreaks = pageBreaks.concat(manualPageBreaks || []).filter(b => b?.at && b?.at[0] !== undefined && b?.at[0] !== null);
		const allBreaks = unsortedPageBreaks.sort((a, b) => a?.at[0] - b?.at[0]);
		const newHeadings = [];
		const nodeLevelsArr = node ? node.levels.map(l => l.type) : levelTypes
		const oldChildren = node ? node.children : tocNode.children

		editor.children.forEach((n, idx) => {
			if ((node && node.trackBefore) || (!before && trackBefore) || (before && before.value)) {
				if (nodeLevelsArr.includes("h1") && n.type === 'heading-one' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
				if (nodeLevelsArr.includes("h2") && n.type === 'heading-two' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
				if (nodeLevelsArr.includes("h3") && n.type === 'heading-three' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
			} else if (idx > tocPath) {
				if (nodeLevelsArr.includes("h1") && n.type === 'heading-one' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
				if (nodeLevelsArr.includes("h2") && n.type === 'heading-two' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
				if (nodeLevelsArr.includes("h3") && n.type === 'heading-three' && n.children && n.children.length > 0 && (!n.children.every((child) => !child.text || isWhitespaceString(child.text)))) {
					newHeadings.push({ ...n, headingIndex: idx });
				}
			}
		});

		const formattedHeadings = newHeadings.map(h => {
			const originalH = editor.children[h.headingIndex]
			const nodeWithPage = determinePage(originalH, h.headingIndex, allBreaks)
			const sharedId = `heading-at-${h?.headingIndex}`
			return {
				type: 'toc-label',
				heading: h.type,
				headingId: sharedId,
				headingIndex: h.headingIndex,
				page: nodeWithPage.page,
				children: [{ text: h.children.map(c => c.text).join(' ') }]
			}
		})
		const newChildren = [...formattedHeadings, { type: 'placeholder', children: [{ text: "placeholder" }] }]

		// console.log("##### oldChildren", oldChildren)
		// console.log("### newChildren", newChildren)

		if (oldChildren.length !== newChildren.length) {
			if (oldChildren.length > newChildren.length) {
				let deletedIndexes = []
				let deletedHeadingIndexes = []
				
				oldChildren.forEach((c, index) => {
					if (c.type === 'toc-label') {
						const hLevel = c.heading === 'heading-one' ? 'h1' : c.heading === 'heading-two' ? 'h2' : c.heading === 'heading-three' ? 'h3' : null
						const deleting1 = !nodeLevelsArr.includes(hLevel)
						const deleting2 = (before && before.value === false) && c.headingIndex <= tocPath[0]
						const deleting3 = (!node && !before)
						const deleting4 = (!before && node && node.trackBefore === false) && c.headingIndex <= tocPath[0]

						if (deleting1) {
							const lastDeletedIndex = deletedIndexes.length > 0 ? deletedIndexes[deletedIndexes.length - 1] : null
							const newIndex = lastDeletedIndex === null ? index : index - deletedIndexes.length
							const alternativePath = [...tocPath, newIndex]
							const theNode = Node.get(editor, alternativePath);
							if (theNode) {
								deletedIndexes.push(newIndex)
								Transforms.removeNodes(editor, { at: alternativePath });
							}
						}
						if (deleting2 || deleting4) {
							function getLabelByHeadingIndex(editor, headingIndex) {
								return Array.from(
									SlateEditor.nodes(editor, {
										at: [],
										match: n => !SlateEditor.isEditor(n) && n.headingIndex === headingIndex,
									})
								);
							}
							const tocLabelsArray = getLabelByHeadingIndex(editor, c.headingIndex);
							const tocLabelArr = tocLabelsArray.length > 0 ? tocLabelsArray[0] : null;

							if (tocLabelArr) {
								Transforms.removeNodes(editor, { at: tocLabelArr[1] });
							}
							if (deleting4) {
								setThePath(tocPath)
								setTimeout(() => {
									updatedAllHeadings(newChildren, deleting4)
								}, 200);
							}
						}
						if (deleting3) {
							const newHeadingIndexArr = newChildren.map(ch => ch.headingIndex).filter(ch => ch !== undefined)
							const lastDeletedHeadingIndex = deletedHeadingIndexes.length > 0 ? deletedHeadingIndexes[deletedHeadingIndexes.length - 1] : null
							const newHeadingIndex = lastDeletedHeadingIndex === null ? c.headingIndex : c.headingIndex - deletedHeadingIndexes.length
							const newIndex = lastDeletedHeadingIndex === null ? index : index - deletedHeadingIndexes.length

							if (newHeadingIndexArr.includes(c.headingIndex)) {
								const updatedNode = newChildren[index]
								const text = updatedNode && updatedNode.children[0].text
								if (c.children[0].text !== text) {
									Transforms.removeNodes(editor, { at: [...tocPath, index] });
									Transforms.insertNodes(editor, updatedNode/*{ ...c, children: updatedNode.children }*/, { at: [...tocPath, index] });
									Transforms.setNodes(editor, { id: `heading-at-${updatedNode.headingIndex}` }, { at: [updatedNode.headingIndex] })

								}
							} else if (lastDeletedHeadingIndex && newHeadingIndexArr.includes(newHeadingIndex)) {
								const sharedId = `heading-at-${newHeadingIndex}`
								Transforms.setNodes(editor, { headingIndex: newHeadingIndex, headingId: sharedId }, { at: [...tocPath, newIndex] });
								Transforms.setNodes(editor, { id: sharedId }, { at: [newHeadingIndex] })

							} else {
								const theNode = Node.get(editor, [...tocPath, newIndex]);
								if (theNode) {
									deletedHeadingIndexes.push(newHeadingIndex)
									Transforms.removeNodes(editor, { at: [...tocPath, newIndex] });
								}
							}
						}

						if (index === oldChildren.length - 2) {
							if (!node && before && before.value === false) {
								setTrackBefore(false)
								Transforms.setNodes(editor, { trackBefore: false }, { at: tocPath });
							}
							if (node && !before) {
								Transforms.setNodes(editor, { levels: node.levels }, { at: tocPath });
							}
							setTimeout(() => {
								updatedAllHeadings(newChildren, deleting3)
							}, 200);
						}
					}
				})

			} else if (oldChildren.length < newChildren.length) {
				const headingOldIndexesArray = oldChildren.map(c => c.headingIndex).filter(c => c !== undefined)
				const headingNewIndexesArray = newChildren.map(c => c.headingIndex).filter(c => c !== undefined)
				const labelsToRemove = headingOldIndexesArray.filter(x => !headingNewIndexesArray.includes(x))
				const adding1 = node && !before && nodeLevelsArr.length > levelTypes.length
				const adding2 = (before && before.value === true)
				const adding3 = (!node && !before)
				const adding4 = (node && !before && thePath !== null && thePath[0] !== tocPath[0])

				newChildren.forEach((c, index) => {
					if (adding1 || adding2 || adding3 || adding4) {
						if (c.type === 'toc-label' && !headingOldIndexesArray.includes(c.headingIndex)) {
							const h = editor.children[c.headingIndex]
							// console.log("### adding toc-label", c.headingIndex, h)
							Transforms.insertNodes(editor, c, { at: [...tocPath, index] });
							Transforms.setNodes(editor, { id: `heading-at-${c.headingIndex}` }, { at: [c.headingIndex] })
							
						}
						if (c.type === 'toc-label' && headingOldIndexesArray.includes(c.headingIndex)) {
							const h = editor.children[c.headingIndex]
							// console.log("### changing toc-label", c.headingIndex, h)
							Transforms.removeNodes(editor, { at: [...tocPath, index] });
							Transforms.insertNodes(editor, c, { at: [...tocPath, index] });
							Transforms.setNodes(editor, { id: `heading-at-${c.headingIndex}` }, { at: [c.headingIndex] })
						}

						if (index === newChildren.length - 1) {
							// console.log("### finished additions")
							if (!node && before && before.value === true) {
								setTrackBefore(true)
								Transforms.setNodes(editor, { trackBefore: true }, { at: tocPath });
							}
							if (node && !before) {
								Transforms.setNodes(editor, { levels: node.levels }, { at: tocPath });
							}
							if (adding4) setThePath(tocPath)
							setTimeout(() => {
								updatedAllHeadings(newChildren, (adding3 || adding4))
							}, 200);
						}
					}
				})
				if (labelsToRemove.length > 0 && ((adding1 || adding2 || adding3 || adding4))) {
					labelsToRemove.forEach(headingIndex => {
						function getLabelByHeadingIndex(editor, headingIndex) {
							return Array.from(
								SlateEditor.nodes(editor, {
									at: [],
									match: n => !SlateEditor.isEditor(n) && n.headingIndex === headingIndex,
								})
							);
						}
						const tocLabelsArray = getLabelByHeadingIndex(editor, headingIndex);
						const tocLabelArr = tocLabelsArray.length > 0 ? tocLabelsArray[0] : null;

						if (tocLabelArr) {
							Transforms.removeNodes(editor, { at: tocLabelArr[1] });
						}
					})
				}
			}
		}

		if (oldChildren.length === newChildren.length) {
			const noPage = oldChildren.some(c => c.page === undefined || c.page === null)

			if ((!node && !before) || noPage) {
				oldChildren.forEach((c, index) => {
					if (c.type === 'toc-label') {
						const updatedNode = newChildren[index]
						const newHeadingIndex = updatedNode && updatedNode.headingIndex
						const text = updatedNode && updatedNode.children[0].text
						const tocLabelPath = [...tocPath, index]

						if (text !== c.children[0].text) {
							Transforms.removeNodes(editor, { at: tocLabelPath });
							Transforms.insertNodes(editor, updatedNode, { at: tocLabelPath });
							Transforms.setNodes(editor, { id: `heading-at-${updatedNode.headingIndex}` }, { at: [updatedNode.headingIndex] })
						}

						if (newHeadingIndex !== c.headingIndex || c.page !== updatedNode.page) {
							Transforms.setNodes(editor, { headingIndex: newHeadingIndex, headingId: `heading-at-${newHeadingIndex}`, page: updatedNode.page }, { at: tocLabelPath })
							Transforms.setNodes(editor, { id: `heading-at-${newHeadingIndex}` }, { at: [newHeadingIndex] })
						}

						if (index === oldChildren.length - 2) {
							setTimeout(() => {
								updatedAllHeadings(newChildren, !node && !before)
							}, 200);
						}
					}
				})
			}
		}

		if (node) {
			const levelsObj = node.levels.reduce((a, c) => ({ ...a, [c.type]: c.indent }), {})
			if (nodeLevelsArr.includes('h1')) {
				setH1Indent(levelsObj['h1'])
			}
			if (nodeLevelsArr.includes('h2')) {
				setH2Indent(levelsObj['h2'])
			}
			if (nodeLevelsArr.includes('h3')) {
				setH3Indent(levelsObj['h3'])
			}
			setLevels(node.levels)
			setLevelTypes(nodeLevelsArr)
			setGapStyle(node.style)
			setTrackBefore(node.trackBefore)
		}

		setHeadings(newHeadings)
	}

	function updateToc(key, value) {
		const tocPath = getTOCLocation()
		const newNode = { ...editor.children[tocPath], [key]: value }

		if (key === 'levels') {
			updateChildren(newNode)
		} else if (key === 'trackBefore') {
			updateChildren(null, { value: value })
		} else {
			const keyName = key === 'indent' ? 'levels' : key
			Transforms.setNodes(editor, { [keyName]: value }, { at: tocPath })
			setGapStyle(value)
		}
	}

	function deleteTOC() {
		const tocPath = getTOCLocation()
		Transforms.removeNodes(editor, { at: tocPath })
	}


	return (
		<div
			contentEditable={false}
			style={{ userSelect: "none" }}
			{...props.attributes}
			className='toc-wrapper-outer'
			onMouseEnter={() => setHovering(true)}
			onMouseLeave={() => setHovering(false)}
		>
			<ContextMenuTrigger id={`table-of-contents-${ToCId}`}>
				<div
					contentEditable={false}
					draggable={true}
					onDragStart={e => {
						e.dataTransfer.setDragImage(tocElement, 0, 0)
						e.target.style.cursor = "grabbing";
						const tocPath = ReactEditor.findPath(editor, tocNode);
						e.dataTransfer.setData('application/x-slate-special-element-path', JSON.stringify(tocPath));
					}}
				>
					<div
						style={{ cursor: "grab", borderRadius: '3pt', border: hovering ? '2px solid #0E8EFD' : '2px solid transparent' }}
						contentEditable={false}
						className='toc-wrapper'
						onMouseDown={(e) => {
							if (editor.selection) {
								Transforms.deselect(editor)
								setTimeout(() => {
									const location = getTOCLocation()
									const path = [location[0], tocNode.children.length - 1, 0]
									const length = tocNode.children[tocNode.children.length - 1].children[0].text.length
									const newSelection = { anchor: { path: path, offset: 0 }, focus: { path: path, offset: length } }
									Transforms.select(editor, newSelection);
								}, 200);
							} else {
								const location = getTOCLocation()
								const path = [location[0], tocNode.children.length - 1, 0]
								const length = tocNode.children[tocNode.children.length - 1].children[0].text.length
								const newSelection = { anchor: { path: path, offset: 0 }, focus: { path: path, offset: length } }
								Transforms.select(editor, newSelection);
							}
						}}
					>
						<div id={"actual-toc-" + ToCId} contentEditable={false} className='actual-toc'>
							{tocNode.children && tocNode.children.length === 1 && tocNode.children[0].type === 'placeholder' &&
								<div style={{ justifyItems: 'center', padding: '12pt', backgroundColor: "#F0F3F8", borderRadius: '3pt', marginBottom: -17 }}><p style={{ width: "fit-content" }}>No headers after Table of Contents</p></div>
							}
							{props.children.map((child) => {
								if (child?.props?.children?.props?.element?.type === 'toc-label') {
									const element = child?.props?.children?.props?.element
									const children = element?.children;
									const path = ReactEditor.findPath(editor, element);
									return (
										<TableOfContentsLine
											children={children}
											editor={editor}
											pageBreaks={pageBreaks}
											manualPageBreaks={manualPageBreaks}
											styles={styles}
											flowStyles={flowStyles}
											element={element}
											elementPath={path}
											passedTocNode={tocNode}
											gapStyle={gapStyle}
										/>
									)
								} else return child
							})}
						</div>
					</div>
				</div>
			</ContextMenuTrigger>
			<div contentEditable={false} style={{ userSelect: "none" }}>
				<ContextMenu id={`table-of-contents-${ToCId}`} style={{ padding: '0.5rem 0.25rem' }}>
					<MenuItem onClick={(e) => e.stopPropagation()} >
						<div onClick={(e) => { e.stopPropagation(); updateChildren(null, null) }} style={{ display: 'flex', width: '100%' }}>
							<i className='material-icons colour-text-primary' style={{ fontSize: 18, marginLeft: -2, marginRight: 5, opacity: 1 }}>update</i>
							<div style={{ fontSize: '0.85rem', fontWeight: 300, color: '#333B5A' }}>Update content</div>
						</div>
					</MenuItem>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<Checkbox
							className={`cursor-default small`}
							checked={trackBefore}
							disabled={headings.length === 0}
							onChange={() => updateToc('trackBefore', !trackBefore)}
							label='Include headings above'
							size='s'
						/>
					</MenuItem>
					<div style={{ padding: '8px 6px 2px 6px', color: '#333B5A', fontSize: '0.85rem', fontWeight: 300, borderTop: '0.5px solid #e2e4eb', display: 'flex', justifyContent: 'space-between' }}>
						<div>Heading levels</div>
						<div style={{ paddingRight: 12 }}>Indent</div>
					</div>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: 30, alignItems: 'center' }}>
							<Checkbox
								className={`cursor-default small`}
								checked={levelTypes.includes("h1")}
								disabled={headings.length === 0}
								onChange={() => {
									if (levelTypes.includes("h1")) {
										setH1Indent(0.00)
										updateToc('levels', levels.filter(el => el.type !== 'h1'))
									} else {
										updateToc('levels', [{ type: "h1", indent: 0.00 }, ...levels])
									}
								}}
								label='H1'
								size='s'
							/>
							<DropDown
								value={h1Indent}
								suffix="pt"
								clickable
								fullWidth
								stopPropagation
								noClickAway
								checkAncestors={{ length: 9, className: 'sketch-picker ' }}
								disabled={!levelTypes.includes("h1")}
								size='extra-small'
								outerStyle={{ width: 65 }}
								popupElementClass='small-item'
							>
								{indentationOptions && indentationOptions.length > 0 && indentationOptions.map((option, index) => <div
									key={"indent-option-" + index}
									onClick={() => {
										setH1Indent(option)
										updateToc('indent', levels.map(l => l.type === 'h1' ? { type: 'h1', indent: option } : l))
									}}
								>{option}</div>)}
							</DropDown>
						</div>
					</MenuItem>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: 30, alignItems: 'center' }}>
							<Checkbox
								className={`cursor-default small`}
								checked={levelTypes.includes("h2")}
								disabled={headings.length === 0}
								onChange={(e) => {
									if (levelTypes.includes("h2")) {
										updateToc('levels', levels.filter(el => el.type !== 'h2'))
										setH2Indent(0.00)
									} else {
										if (levels.length === 1) {
											if (levels[0].type === 'h1') updateToc('levels', [...levels, { type: 'h2', indent: 0.00 }])
											else updateToc('levels', [{ type: 'h2', indent: 0.00 }, ...levels])
										} else updateToc('levels', [levels[0], { type: 'h2', indent: 0.00 }, levels[1]])
									}
								}}
								label='H2'
								size='s'
							/>
							<DropDown
								value={h2Indent}
								suffix="pt"
								clickable
								fullWidth
								stopPropagation
								noClickAway
								checkAncestors={{ length: 9, className: 'sketch-picker ' }}
								disabled={!levelTypes.includes("h2")}
								size='extra-small'
								outerStyle={{ width: 65 }}
								popupElementClass='small-item'
							>
								{indentationOptions && indentationOptions.length > 0 && indentationOptions.map((option, index) => <div
									key={"indent-option-" + index}
									onClick={() => {
										setH2Indent(option)
										updateToc('indent', levels.map(l => l.type === 'h2' ? { type: 'h2', indent: option } : l))
									}}
								>{option}</div>)}
							</DropDown>
						</div>
					</MenuItem>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', justifyContent: 'space-between', width: '100%', height: 30, alignItems: 'center' }}>
							<Checkbox
								className={`cursor-default small`}
								checked={levelTypes.includes("h3")}
								disabled={headings.length === 0}
								onChange={(e) => {
									if (levelTypes.includes("h3")) {
										updateToc('levels', levels.filter(el => el.type !== 'h3'))
										setH3Indent(0.00)
									} else updateToc('levels', [...levels, { type: 'h3', indent: 0.00 }])
								}}
								label='H3'
								size='s'
							/>
							<DropDown
								value={h3Indent}
								suffix="pt"
								clickable
								fullWidth
								stopPropagation
								noClickAway
								checkAncestors={{ length: 9, className: 'sketch-picker ' }}
								disabled={!levelTypes.includes("h3")}
								size='extra-small'
								outerStyle={{ width: 65 }}
								popupElementClass='small-item'
							>
								{indentationOptions && indentationOptions.length > 0 && indentationOptions.map((option, index) => <div
									key={"indent-option-" + index}
									onClick={() => {
										setH3Indent(option)
										updateToc('indent', levels.map(l => l.type === 'h3' ? { type: 'h3', indent: option } : l))
									}}
								>{option}</div>)}
							</DropDown>
						</div>
					</MenuItem>
					<div style={{ padding: '8px 6px 2px 6px', color: '#333B5A', fontWeight: 300, fontSize: '0.85rem', borderTop: '0.5px solid #e2e4eb' }}>Gap style</div>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', width: '100%' }}>
							<input checked={gapStyle === 'solid'} type="radio" onClick={(e) => { e.stopPropagation(); updateToc('style', 'solid') }} id="solid" name="gap-style" value="solid" />
							<label style={{ paddingLeft: 8.5 }} for="solid">Solid</label>
						</div>
					</MenuItem>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', width: '100%' }}>
							<input checked={gapStyle === 'dotted'} type="radio" onClick={(e) => { e.stopPropagation(); updateToc('style', 'dotted') }} id="dotted" name="gap-style" value="dotted" />
							<label style={{ paddingLeft: 8.5 }} for="dotted">Dotted</label>
						</div>
					</MenuItem>
					<MenuItem onClick={(e) => e.stopPropagation()}>
						<div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', width: '100%' }}>
							<input checked={gapStyle === 'transparent'} type="radio" onClick={(e) => { e.stopPropagation(); updateToc('style', 'transparent') }} id="transparent" name="gap-style" value="transparent" />
							<label style={{ paddingLeft: 8.5 }} for="transparent">Transparent</label>
						</div>
					</MenuItem>
					<MenuItem onClick={deleteTOC}>
						<Trash2 size={16} className='opacity-06' style={{ marginLeft: -1.5, marginRight: 6.5 }} />
						<div style={{ fontSize: '0.85rem', fontWeight: 300, color: '#333B5A' }}>Delete</div>
					</MenuItem>
				</ContextMenu>
			</div>
		</div>
	);
}

export default TableOfContents;